import {
  useState,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Activity, DollarSign, Info, Layers, TrendingDown } from "./icons";
import { Tooltip } from "../../design-system/components";

/* The Figma overview's KPI card kit (nodes 284:315 / 284:3227): the mint
 * cost-reduction feature card with its tick-gauge strip, and the soft-grey
 * stat cards. Shared by the workspace Overview and the per-agent detail page,
 * which render the same four cards from different data sources. */

export type KpiIcon = ComponentType<{
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
}>;

/* Mint "Savings" feature card: label + trend icon head, decorative tick gauge, and the
 * big percentage with an optional mono caption (e.g. "$103.98 saved"). */
export function CostReductionCard({
  pct,
  caption,
  hint,
}: {
  /** Saved share of gross spend, 0–100. */
  pct: number;
  caption?: string;
  hint?: ReactNode;
}) {
  return (
    <div
      className="tw-float-up flex h-[170px] flex-col justify-between rounded-[5px] px-4 pb-5 pt-4"
      // The mint ground is fixed across themes, so the ink is pinned to the
      // light theme's text color — var(--text) would go light-on-light on dark.
      style={{ background: "var(--savings-card-bg)", color: "#140309" }}
    >
      <KpiHead label="Savings" Icon={TrendingDown} hint={hint} />
      <TickGauge frac={pct / 100} />
      <div className="flex items-end justify-between gap-2">
        <span className="text-[40px] font-light leading-none tabular-nums">
          {pct.toFixed(0)}%
        </span>
        {caption && (
          <span className="tw-mono text-[9px] leading-none">{caption}</span>
        )}
      </div>
    </div>
  );
}

/* Plain stat card — soft-grey ground, label + icon head, and the big light
 * numeral bottom-left with the mono sub-stat bottom-right. */
export function StatCard({
  label,
  Icon,
  value,
  floatDelay = 0,
  hint,
  children,
}: {
  label: string;
  Icon: KpiIcon;
  value: string;
  /** Entrance stagger (seconds) — see .tw-float-up in app-theme.css. */
  floatDelay?: number;
  hint?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div
      className="tw-float-up flex h-[170px] flex-col justify-between rounded-[5px] px-4 pb-5 pt-4"
      style={
        {
          background: "var(--surface-2)",
          color: "var(--text)",
          "--float-delay": `${floatDelay}s`,
        } as CSSProperties
      }
    >
      <KpiHead label={label} Icon={Icon} hint={hint} />
      <div className="flex items-end justify-between gap-2">
        <span className="text-[40px] font-light leading-none tabular-nums">
          {value}
        </span>
        {children}
      </div>
    </div>
  );
}

export function KpiHead({
  label,
  Icon,
  hint,
}: {
  label: string;
  Icon: KpiIcon;
  /** Optional explainability copy — renders an info affordance beside the label. */
  hint?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-1.5">
        {/* Explicit inherit: the DS base layer paints bare <p> in --ai21-fg-1
            (light on dark), which would beat the card's inherited ink. */}
        <p
          className="cursor-default text-[15px] leading-none"
          style={{ color: "inherit" }}
        >
          {label}
        </p>
        {hint && <MetricHint label={label} hint={hint} />}
      </div>
      <Icon size={15} strokeWidth={1.6} className="mt-0.5 flex-shrink-0" />
    </div>
  );
}

/* Info affordance for a metric's explainability copy (OPSS-238). A real button
 * so it's keyboard-reachable — the DS Tooltip is CSS-only and opens on
 * :focus-within, which needs a natively focusable trigger. Opens down and to the
 * right: the KPI heads sit at the top of the first grid row, so a top-side bubble
 * clips above the fold and a centered one clips off the left edge on the leftmost
 * (Savings) card. */
export function MetricHint({
  label,
  hint,
}: {
  label: string;
  hint: ReactNode;
}) {
  return (
    <Tooltip
      content={hint}
      side="bottom"
      align="start"
      className="max-w-[300px]"
    >
      <button
        type="button"
        aria-label={`About ${label}`}
        className="flex items-center opacity-60 transition-opacity hover:opacity-100 focus-visible:opacity-100"
      >
        <Info size={13} strokeWidth={1.8} />
      </button>
    </Tooltip>
  );
}

export function SubStat({
  val,
  unit,
  note,
  onNoteClick,
  noteLabel,
}: {
  val: string;
  unit: string;
  /* Optional muted qualifier, e.g. "3 failed" under a request count. Rendered only
     when set, so a clean window keeps the original two-part shape. */
  note?: string;
  /* Makes the note a real button — the drill-down into what the qualifier
     summarizes (OPSS-352). Absent, the note stays the plain span it was. The
     card itself deliberately stays inert: a whole-card target would swallow the
     MetricHint button in its head. */
  onNoteClick?: () => void;
  /** What the note button leads to, appended to the note itself to form the
   *  accessible name ("4 failed — View failed requests"). */
  noteLabel?: string;
}) {
  return (
    <span className="tw-mono text-[9px] leading-none">
      <span className="tabular-nums">{val}</span>{" "}
      <span style={{ color: "var(--text-muted)" }}>{unit}</span>
      {note ? (
        <span style={{ color: "var(--text-muted)" }}>
          {" · "}
          {onNoteClick ? (
            // Same focus treatment as MetricHint: the note is muted at rest and
            // comes up to full ink on hover or keyboard focus.
            <button
              type="button"
              // The label EXTENDS the visible note, never replaces it (WCAG
              // 2.5.3): "4 failed" is the only text on screen, so a speech-input
              // user saying "click 4 failed" has to reach this control, and a
              // screen-reader user must still hear the count. Unlabelled, the
              // text content is already the name — leave the attribute off.
              aria-label={noteLabel ? `${note} — ${noteLabel}` : undefined}
              onClick={onNoteClick}
              className="underline underline-offset-2 opacity-80 transition-opacity hover:opacity-100 focus-visible:opacity-100"
            >
              {note}
            </button>
          ) : (
            note
          )}
        </span>
      ) : null}
    </span>
  );
}

/* Mono label/value rows for the Tokens card (Input / Output / Cache). */
export function TokenBreakdown({ rows }: { rows: [string, string][] }) {
  return (
    <div className="flex flex-col gap-[5px]">
      {rows.map(([label, val]) => (
        <span
          key={label}
          className="tw-mono flex gap-1.5 text-[9px] leading-none"
        >
          <span style={{ color: "var(--text-muted)" }}>{label}</span>
          <span className="tabular-nums">{val}</span>
        </span>
      ))}
    </div>
  );
}

/* The design's tick-strip gauge: a row of thin vertical ticks, filled from the
 * left in proportion to the saved share. Purely decorative (the numeral below
 * carries the value). On hover the strip plays: the tick under the cursor
 * stretches tall and its neighbours duck progressively lower, a bell curve
 * that follows the pointer. */
export function TickGauge({ frac }: { frac: number }) {
  const N = 48;
  const filled = Math.round(Math.max(0, Math.min(1, frac)) * N);
  const [hoverI, setHoverI] = useState<number | null>(null);

  // Bell profile around the hovered tick (σ ≈ 2.5 ticks): the peak reaches
  // ~1.45×, the tails settle slightly below resting height.
  const scaleOf = (i: number) => {
    if (hoverI === null) return 1;

    const d = i - hoverI;
    return 0.75 + 0.7 * Math.exp(-(d * d) / (2 * 2.5 * 2.5));
  };

  return (
    <div
      aria-hidden
      className="flex h-[20px] items-stretch justify-between"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        if (!r.width) return;

        const i = Math.round(((e.clientX - r.left) / r.width) * (N - 1));
        setHoverI(Math.max(0, Math.min(N - 1, i)));
      }}
      onMouseLeave={() => setHoverI(null)}
    >
      {Array.from({ length: N }, (_, i) => (
        <span
          key={i}
          className="tw-bar-up w-[2px] rounded-full"
          style={
            {
              // currentColor: follow the card's pinned ink, not the theme text.
              background:
                i < filled
                  ? "currentColor"
                  : "color-mix(in srgb, currentColor 22%, transparent)",
              // Each tick grows up, sweeping left to right across the strip.
              "--bar-delay": `${0.15 + (i / N) * 0.4}s`,
              transform: `scaleY(${scaleOf(i)})`,
              transformOrigin: "bottom",
              transition: "transform 140ms ease",
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

/* Both pages that use this row show the same four metrics, so the labels are
 * known before the numbers are: keep them and pulse the card (OPSS-323). */
const SKELETON_CARDS: [string, KpiIcon][] = [
  ["Savings", TrendingDown],
  ["Spend", DollarSign],
  ["Sessions", Activity],
  ["Tokens", Layers],
];

export function KpiSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {SKELETON_CARDS.map(([label, Icon]) => (
        <div
          key={label}
          className="flex h-[170px] animate-pulse flex-col justify-between rounded-[5px] px-4 pb-5 pt-4"
          style={{ background: "var(--surface-2)", color: "var(--text)" }}
        >
          <KpiHead label={label} Icon={Icon} />
        </div>
      ))}
    </div>
  );
}
